import type { FlexProps } from '@chakra-ui/react';
import {
  Box,
  Button,
  Flex,
  Icon,
  Image,
  Skeleton,
  Text,
} from '@chakra-ui/react';
import { InDesktop } from '@ui/components/MobileOrDesktop';
import { RotatingBg } from '@ui/components/RotatingBg';
import useVnoBasicInfo from '@ui/hooks/useVnoBasicInfo';
import { useTranslations } from '@ui/i18n';
import { useVenoSDK } from '@ui/providers/VenoSDKProvider';
import { formatNumber, formatTokenAmount } from '@ui/utils/format';
import { ArrowBack } from '@veno-app/icons';
import { currentWallet } from '@veno-app/wallet';
import React from 'react';

import { RESERVOIR_REWARD_TOKEN_DICT } from './constants';
import { useVaultDetailDrawer } from './ReservoirAndFountain';
import type { VaultType } from './ReservoirAndFountain/types';

type LockVnoCardProps = {
  type: VaultType;
} & FlexProps;

const { useIsZksyncChainId } = currentWallet;

const LockVnoCard = ({ type, ...flexProps }: LockVnoCardProps) => {
  const t = useTranslations();
  const isZksync = useIsZksyncChainId();
  const sdk = useVenoSDK();
  const rewardToken = RESERVOIR_REWARD_TOKEN_DICT[sdk.chainType];

  const { isLoading, totalStakes, maxApr } = useVnoBasicInfo(type);
  const onVaultDetailDrawerOpen = useVaultDetailDrawer((s) => s.onOpen);

  const isReservoir = type === 'RESERVOIR';

  const name = isReservoir ? 'Reservoir' : 'Fountain';

  return (
    <Flex
      h="calc(100% - 20px)"
      flexDir="column"
      my={2}
      justify="space-between"
      shadow="card.normal"
      cursor="pointer"
      p={4}
      bg="bg.card"
      rounded="5px"
      position="relative"
      onClick={() => {
        onVaultDetailDrawerOpen(type);
      }}
      {...flexProps}
    >
      <Flex justify="space-between">
        <Flex flexDir="column" color="text.light">
          <Text textStyle="h4Bold" color="text.normal">
            {name}
          </Text>
          <Box mt={1} textStyle="body">
            APR:{' '}
            {!isLoading ? (
              t('Up to {num} in {coin}', {
                num: (maxApr > 0 ? formatNumber(maxApr * 100, 0) : '--') + '%',
                coin: type === 'RESERVOIR' ? rewardToken.name : 'VNO',
              })
            ) : (
              <Skeleton display="inline-block" h="19px" w="50px" />
            )}
          </Box>
        </Flex>
        <Box
          w="48px"
          position="relative"
          rounded="full"
          h="48px"
          shadow="card.active"
        >
          <RotatingBg boxSize="48px" position="absolute" zIndex={0} />
          <Box position="absolute" left="8px" top="8px">
            <Image
              alt="logo"
              src={type === 'RESERVOIR' ? rewardToken.icon : '/tokens/vno.svg'}
              w="32px"
              h="32px"
            />
          </Box>
        </Box>
      </Flex>
      <Text mt={2} color="text.light" textStyle="bodySmall">
        {type === 'RESERVOIR' ? (
          <>
            {t(
              isZksync
                ? 'Deposit your VNO to earn protocol rewards in ETH'
                : 'Deposit your VNO to earn protocol rewards in CRO and ATOM',
            )}
          </>
        ) : (
          <>
            {t(
              'Lock your VNO in the Fountain to earn a share of VNO emissions',
            )}
          </>
        )}
      </Text>
      <Box mt={4} textStyle="body" color="text.light">
        {t('Locked')}:{' '}
        {!isLoading ? (
          <Text fontWeight="bold" as="span" color="text.normal">
            {formatTokenAmount(totalStakes, 2)}
          </Text>
        ) : (
          <Skeleton display="inline-block" h="19px" w="70px" />
        )}
      </Box>
      <InDesktop>
        <Button
          variant="raised"
          size="xs"
          position="absolute"
          right="12px"
          bottom="16px"
        >
          <Icon as={ArrowBack} boxSize="18px" transform="rotate(180deg)" />
        </Button>
      </InDesktop>
    </Flex>
  );
};
export default LockVnoCard;
